// src/components/Sections/Admin/Ventas/DetalleVentaDrawer.jsx
//
// Detalle de una venta (orden online o venta presencial). Lo abren Reportería › Ventas
// unificadas y la ficha de usuario.
//
// Cancelar SOLO se ofrece en ventas que todavía no se pagan. Antes aparecía en todo lo
// que no estuviera cancelado o expirado —incluidas ventas completadas— y `cancelarVenta`
// del backend no revisa el estado: una venta pagada quedaba "cancelada" con sus
// inscripciones activas y la plata cobrada. Revertir una venta pagada es ANULAR
// (Ventas unificadas), que es otro flujo.
import React, { useState, useEffect } from 'react';
import { Drawer, Descriptions, Spin, Alert, Table, Modal, Input } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { FiShoppingBag, FiMonitor, FiFileText, FiX, FiSlash, FiInfo } from 'react-icons/fi';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { limpiarNombre } from '../../../../utils/formatters';
import OrdenCompraSvc from '../../../../service/OrdenCompra';
import VentaPresencialSvc from '../../../../service/VentaPresencial';
import { useNotify } from '../../../../providers/NotificationProvider';
import { claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';

const formatCLP = (n) => (n != null ? `$${Number(n).toLocaleString('es-CL')}` : '–');
const MOTIVO_MIN = 5;

// azul = al día · amarillo = en curso/atención · gris = terminado · rojo = falló
const TONOS = {
  azul: 'bg-blue-50 text-primary',
  amarillo: 'bg-secondary/40 text-[#5c4300]',
  gris: 'bg-gray-100 text-gray-600',
  rojo: 'bg-red-50 text-red-700',
};
const Chip = ({ tono = 'gris', children }) => (
  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${TONOS[tono]}`}>{children}</span>
);

const ESTADO_ONLINE = {
  completada: ['azul', 'Completada'], creada: ['gris', 'Creada'], procesando_pago: ['amarillo', 'Procesando pago'],
  fallida: ['rojo', 'Fallida'], cancelada: ['gris', 'Cancelada'],
};
// `requiere_revision`: la plata entró pero la venta no alcanzó a completarse. Antes
// se mostraba el valor crudo.
const ESTADO_PRESENCIAL = {
  completada: ['azul', 'Completada'], en_proceso: ['amarillo', 'En proceso'], cancelada: ['gris', 'Cancelada'],
  expirada: ['gris', 'Expirada'], requiere_revision: ['rojo', 'Pago en revisión'],
};
const chip = (mapa, v) => { const [t, l] = mapa[v] || ['gris', v || '—']; return <Chip tono={t}>{l}</Chip>; };

// Ventas que se pueden cancelar: las que todavía no se cobran.
const CANCELABLE_ONLINE = ['creada', 'procesando_pago'];
const CANCELABLE_PRESENCIAL = ['en_proceso'];
const PAGADA = ['completada', 'requiere_revision'];

const TIPO_PAGO = { efectivo: 'Efectivo', debito: 'Débito', credito: 'Crédito', webpay_qr: 'Webpay (QR)', webpay: 'Webpay', gratuito: 'Gratuito' };

/** La URL pública de la boleta; `urlPdf` es el enlace privado de Bsale y no abre sin sesión. */
const urlBoleta = (doc) => doc?.urlPublica || doc?.urlPdf;

/** Normaliza nombres para cruzar el documento con el ítem (Bsale guarda el nombre tal cual se envió). */
const mismaPersona = (a, b) =>
  String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/** El bloque horario legible, sin asumir el formato: hay órdenes viejas con `dias/horaInicio`. */
const textoHorario = (horario) => {
  if (!horario) return null;
  const b = (horario.bloques || [])[0];
  if (b) return `${(b.dias || []).join('/')} ${b.inicio}-${b.fin}`;
  if (horario.dias) return `${(horario.dias || []).join('/')} ${horario.horaInicio}-${horario.horaFin}`;
  return null;
};

const Seccion = ({ titulo, children }) => (
  <section className="rounded-3xl bg-white border border-gray-100 p-5">
    <h3 className="text-xs font-extrabold uppercase tracking-widest text-gray-900 mb-3">{titulo}</h3>
    {children}
  </section>
);

const ItemsOnline = ({ items, documentos = [] }) => {
  const columns = [
    {
      title: 'Producto',
      render: (_, r) => {
        // El backend popula `items.taller` como objeto; `tallerNombre` no existe en el
        // ítem, así que leerlo solo a él dejaba la columna en "–" para toda orden online.
        const nombre = r.taller?.nombre || r.tallerNombre || r.descripcion
          || (r.tipo === 'matricula' ? `Matrícula ${r.recinto?.nombre || ''}`.trim() : null);
        const horario = textoHorario(r.horario);
        return (
          <div className="min-w-0">
            <div className="font-bold text-gray-900">{nombre || '–'}</div>
            <div className="text-xs text-gray-500">
              {r.tipo === 'matricula' ? 'Matrícula' : 'Taller'}
              {horario && ` · ${horario}`}{r.recinto?.nombre && ` · ${r.recinto.nombre}`}
            </div>
            {r.inscrito?.nombre && (
              <div className="text-xs text-gray-500">Para {limpiarNombre(r.inscrito.nombre)} ({r.inscrito.rut})</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Plan',
      dataIndex: ['planSeleccionado', 'plan'],
      width: 110,
      render: (v) => (v ? <span className="capitalize text-sm">{v}</span> : '–'),
    },
    {
      title: 'Total',
      align: 'right',
      width: 120,
      render: (_, r) => (
        <div>
          <div className="font-bold tabular-nums">{formatCLP(r.montoTotal ?? r.subtotal)}</div>
          {r.planSeleccionado?.descuentos?.totalDescuento > 0 && (
            <div className="text-xs text-red-600 tabular-nums">−{formatCLP(r.planSeleccionado.descuentos.totalDescuento)}</div>
          )}
        </div>
      ),
    },
    {
      // En una orden parcial importa ver CUÁL ítem quedó sin boleta.
      title: 'Boleta',
      width: 120,
      render: (_, r) => {
        const doc = documentos.find(d =>
          mismaPersona(d.inscrito, r.inscrito?.nombre) &&
          (d.tipo === 'matricula') === (r.tipo === 'matricula'));
        if (!doc) return <Chip tono="rojo">Sin boleta</Chip>;
        const url = urlBoleta(doc);
        return url
          ? <a href={url} target="_blank" rel="noreferrer" className="text-sm font-bold text-primary hover:underline">N° {doc.numeroDocumento}</a>
          : <span className="text-sm">N° {doc.numeroDocumento}</span>;
      },
    },
  ];
  return (
    <div className="admin-table-card">
      <Table dataSource={items} columns={columns} rowKey={(_, i) => i} size="middle" pagination={false} scroll={{ x: 520 }} />
    </div>
  );
};

const Documentos = ({ documentos }) => (
  documentos?.length > 0 ? (
    <ul className="m-0 p-0 list-none space-y-2">
      {documentos.map((doc, i) => {
        const url = urlBoleta(doc);
        return (
          <li key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-gray-50">
            <span className="w-8 h-8 flex-shrink-0 rounded-lg bg-white text-primary flex items-center justify-center"><FiFileText size={15} /></span>
            <span className="flex-1 min-w-0 text-sm">
              <span className="block font-semibold text-gray-900 truncate">{doc.taller || `Documento ${i + 1}`}</span>
              <span className="block text-xs text-gray-500">
                {doc.numeroDocumento && `N° ${doc.numeroDocumento}`}{doc.monto ? ` · ${formatCLP(doc.monto)}` : ''}
              </span>
            </span>
            {url && (
              <a href={url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-bold text-gray-700 hover:border-primary hover:!text-primary">
                <FiFileText size={12} /> Ver boleta
              </a>
            )}
          </li>
        );
      })}
    </ul>
  ) : <p className="text-sm text-gray-500 m-0">Sin boletas emitidas.</p>
);

/* ── Componente principal ─────────────────────────────────────────────────── */

const DetalleVentaDrawer = ({ venta, onClose, onCancelada }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState(null);
  const [cancelModal, setCancelModal] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cancelando, setCancelando] = useState(false);

  const open = !!venta;
  const esOnline = venta?.tipoVenta === 'online';
  const angosto = typeof window !== 'undefined' && window.innerWidth < 800;

  useEffect(() => {
    if (!venta?.id) return;
    setLoading(true);
    setError(null);
    setDetalle(null);
    const fetch = esOnline
      ? OrdenCompraSvc.GetDetalle(venta.id).then(r => r?.data ?? r)
      : VentaPresencialSvc.ObtenerVenta(venta.id).then(r => r?.data?.data ?? r?.data ?? r);
    fetch
      .then(setDetalle)
      .catch(() => setError('No se pudo cargar el detalle.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venta?.id]);

  const estado = detalle?.estado;
  const puedeCancelar = !!detalle && (esOnline ? CANCELABLE_ONLINE : CANCELABLE_PRESENCIAL).includes(estado);
  const estaPagada = !!detalle && PAGADA.includes(estado);

  const handleCancelar = async () => {
    const texto = motivo.trim();
    if (texto.length < MOTIVO_MIN) return;
    setCancelando(true);
    try {
      if (esOnline) await OrdenCompraSvc.Cancelar(venta.id, texto);
      else await VentaPresencialSvc.CancelarVenta(venta.id, { motivo: texto });
      notify.success('Venta cancelada');
      setCancelModal(false);
      setMotivo('');
      onCancelada?.();
      onClose();
    } catch (e) {
      notify.error(e?.error || e?.message || 'No se pudo cancelar la venta');
    } finally {
      setCancelando(false);
    }
  };

  const total = detalle ? (esOnline ? detalle.resumen?.total : detalle.montoTotal) : null;
  const fecha = detalle && (detalle.fechaCompletada || detalle.fechaPago || detalle.createdAt);
  const cliente = detalle ? (esOnline ? (detalle.datosCliente || {}) : (detalle.datosUsuario || detalle.inscrito || {})) : {};
  const mp = detalle?.metodoPago;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={angosto ? '100%' : 760}
        destroyOnClose
        closable={false}
        title={null}
        styles={{ header: { display: 'none' }, body: { padding: 0, background: '#f4f6f9' } }}
      >
        {/* ── Cabecera ── */}
        <header className="relative bg-[#00266b] text-white px-6 pt-6 pb-5">
          <button type="button" onClick={onClose} aria-label="Cerrar detalle"
            className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-full text-blue-100 hover:bg-white/10 hover:text-white transition-colors">
            <FiX size={18} />
          </button>
          <div className="flex items-center gap-3 pr-10">
            <span className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center">
              {esOnline ? <FiMonitor size={20} /> : <FiShoppingBag size={20} />}
            </span>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-widest text-secondary m-0">{esOnline ? 'Orden online' : 'Venta presencial'}</p>
              <p className="text-lg font-extrabold m-0 font-mono truncate">{venta?.codigo}</p>
            </div>
          </div>
          {detalle && (
            <div className="flex flex-wrap items-end justify-between gap-4 mt-5 pt-4 border-t border-white/10">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-blue-200 m-0">Total</p>
                <p className="text-3xl font-extrabold m-0 tabular-nums">{formatCLP(total)}</p>
              </div>
              <div className="text-right">
                <div className="[&>span]:!bg-white [&>span]:!text-[#00266b]">{chip(esOnline ? ESTADO_ONLINE : ESTADO_PRESENCIAL, estado)}</div>
                {fecha && <p className="text-xs text-blue-100 m-0 mt-1.5">{dayjs(fecha).format('DD/MM/YYYY HH:mm')}</p>}
              </div>
            </div>
          )}
        </header>

        <div className="p-5 sm:p-6 space-y-4">
          {loading && <div className="py-16 text-center"><Spin size="large" /></div>}
          {error && !loading && <Alert message={error} type="error" showIcon />}

          {detalle && !loading && (
            <>
              {estado === 'requiere_revision' && (
                <Alert type="error" showIcon message="Pago en revisión"
                  description="El dinero entró, pero la venta no alcanzó a completarse. Revísala en Operaciones antes de volver a cobrar." />
              )}

              <Seccion titulo="Cliente">
                <Descriptions size="small" column={{ xs: 1, sm: 2 }} colon={false}
                  labelStyle={{ color: '#6b7280', fontSize: 12 }} contentStyle={{ fontSize: 13, fontWeight: 600 }}>
                  <Descriptions.Item label="Nombre">{limpiarNombre(cliente.nombre) || '–'}</Descriptions.Item>
                  <Descriptions.Item label="RUT">{cliente.rut || '–'}</Descriptions.Item>
                  <Descriptions.Item label="Email">{cliente.email || '–'}</Descriptions.Item>
                  {cliente.telefono && <Descriptions.Item label="Teléfono">{cliente.telefono}</Descriptions.Item>}
                </Descriptions>
              </Seccion>

              <Seccion titulo={esOnline ? 'Ítems de la orden' : 'Taller'}>
                {esOnline ? (
                  <>
                    <ItemsOnline items={detalle.items || []}
                      documentos={detalle.documentosBoleta || detalle.pago?.facturacion?.documentosGenerados || []} />
                    {detalle.resumen && (
                      <dl className="ml-auto mt-3 max-w-[260px] space-y-1 text-sm m-0">
                        <div className="flex justify-between"><dt className="text-gray-500">Subtotal</dt><dd className="m-0 tabular-nums">{formatCLP(detalle.resumen.subtotal)}</dd></div>
                        {detalle.resumen.totalDescuentos > 0 && (
                          <div className="flex justify-between"><dt className="text-gray-500">Descuentos</dt><dd className="m-0 text-red-600 tabular-nums">−{formatCLP(detalle.resumen.totalDescuentos)}</dd></div>
                        )}
                        <div className="flex justify-between pt-1 border-t border-gray-100 font-extrabold"><dt>Total</dt><dd className="m-0 tabular-nums">{formatCLP(detalle.resumen.total)}</dd></div>
                      </dl>
                    )}
                  </>
                ) : (
                  <div>
                    <p className="font-bold text-gray-900 m-0">{detalle.taller?.nombre || '–'}</p>
                    <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 m-0 text-sm">
                      <div><dt className="text-xs text-gray-500">Inscrito</dt><dd className="m-0 font-semibold">{limpiarNombre(detalle.inscrito?.nombre)}<span className="block text-xs font-normal text-gray-500">{detalle.inscrito?.rut}</span></dd></div>
                      <div><dt className="text-xs text-gray-500">Plan</dt><dd className="m-0 font-semibold capitalize">{detalle.plan?.nombre || '–'}</dd></div>
                      <div><dt className="text-xs text-gray-500">Valor original</dt><dd className="m-0 font-semibold tabular-nums">{formatCLP(detalle.plan?.valorOriginal)}</dd></div>
                      {detalle.plan?.descuentos?.totalDescuento > 0 && (
                        <div><dt className="text-xs text-gray-500">Descuento</dt><dd className="m-0 font-semibold text-red-600 tabular-nums">−{formatCLP(detalle.plan.descuentos.totalDescuento)}</dd></div>
                      )}
                    </dl>
                    {detalle.vendedor?.nombre && <p className="text-xs text-gray-500 mt-3 mb-0">Vendió: {detalle.vendedor.nombre}</p>}
                  </div>
                )}
              </Seccion>

              <Seccion titulo="Pago">
                {esOnline && detalle.pago && (
                  <Descriptions size="small" column={{ xs: 1, sm: 2 }} colon={false} className="mb-3"
                    labelStyle={{ color: '#6b7280', fontSize: 12 }} contentStyle={{ fontSize: 13, fontWeight: 600 }}>
                    <Descriptions.Item label="Método">{TIPO_PAGO[detalle.pago.metodoPago] || detalle.pago.metodoPago || 'Webpay'}</Descriptions.Item>
                    <Descriptions.Item label="Estado">
                      {detalle.pago.estado === 'aprobado' ? <Chip tono="azul">Aprobado</Chip>
                        : detalle.pago.estado === 'rechazado' ? <Chip tono="rojo">Rechazado</Chip> : <Chip tono="amarillo">Pendiente</Chip>}
                    </Descriptions.Item>
                    <Descriptions.Item label="Monto pagado">{formatCLP(detalle.pago.montoFinal)}</Descriptions.Item>
                    {detalle.pago.webpay?.authorizationCode && <Descriptions.Item label="Autorización">{detalle.pago.webpay.authorizationCode}</Descriptions.Item>}
                    {detalle.pago.webpay?.cardNumber && <Descriptions.Item label="Tarjeta">•••• {detalle.pago.webpay.cardNumber}</Descriptions.Item>}
                  </Descriptions>
                )}
                {!esOnline && mp && (
                  <Descriptions size="small" column={{ xs: 1, sm: 2 }} colon={false} className="mb-3"
                    labelStyle={{ color: '#6b7280', fontSize: 12 }} contentStyle={{ fontSize: 13, fontWeight: 600 }}>
                    <Descriptions.Item label="Método">{TIPO_PAGO[mp.tipo] || mp.tipo || '–'}</Descriptions.Item>
                    {mp.detallesPOS?.numeroVoucher && <Descriptions.Item label="N° voucher">{mp.detallesPOS.numeroVoucher}</Descriptions.Item>}
                    {mp.detallesPOS?.codigoAutorizacion && <Descriptions.Item label="Autorización">{mp.detallesPOS.codigoAutorizacion}</Descriptions.Item>}
                    {mp.detallesPOS?.ultimosDigitosTarjeta && <Descriptions.Item label="Tarjeta">•••• {mp.detallesPOS.ultimosDigitosTarjeta}</Descriptions.Item>}
                  </Descriptions>
                )}
                <Documentos documentos={esOnline ? detalle.documentosBoleta : (detalle.facturacion?.documentosGenerados || [])} />
              </Seccion>

              {/* ── Acciones ── */}
              {puedeCancelar ? (
                <div className="flex justify-end">
                  <button type="button" onClick={() => setCancelModal(true)}
                    className={`${claseBtnSecundario} hover:!border-red-300 hover:!text-red-600`}>
                    <FiSlash size={14} /> Cancelar venta
                  </button>
                </div>
              ) : estaPagada && (
                <p className="flex items-start gap-2 text-xs text-gray-500 m-0">
                  <FiInfo size={13} className="mt-0.5 flex-shrink-0" />
                  <span>
                    Esta venta ya está pagada, así que no se puede cancelar. Para revertirla hay que anularla desde{' '}
                    <Link to="/admin/reporteria/ventas-unificadas" className="font-bold text-primary">Ventas unificadas</Link>.
                  </span>
                </p>
              )}
            </>
          )}
        </div>
      </Drawer>

      <Modal
        title="Cancelar venta"
        open={cancelModal}
        onCancel={() => { setCancelModal(false); setMotivo(''); }}
        onOk={handleCancelar}
        okText="Cancelar venta"
        okButtonProps={{ danger: true, loading: cancelando, disabled: motivo.trim().length < MOTIVO_MIN }}
        cancelText="Volver"
        width={460}
      >
        <p className="text-sm">¿Cancelar la {esOnline ? 'orden' : 'venta'} <strong className="font-mono">{venta?.codigo}</strong>? Todavía no está pagada: se libera el cupo reservado.</p>
        <label htmlFor="motivo-cancelacion" className="block text-sm font-bold text-gray-900 mt-3 mb-1.5">Motivo <span className="text-red-600">*</span></label>
        <Input.TextArea id="motivo-cancelacion" placeholder="Ej: el cliente desistió, error al elegir el taller…"
          value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} maxLength={300} showCount />
        {cancelando && <p className="text-xs text-gray-500 mt-2 mb-0"><LoadingOutlined /> Cancelando…</p>}
      </Modal>
    </>
  );
};

export default DetalleVentaDrawer;
